import { ASSET_CLASS_IDS } from "~/consts";

// // // //

function _isStablecoin(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.Stablecoin;
}

function _isTreasuryProduct(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.TreasuryProduct;
}

function _isCommodity(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.Commodities;
}

function _isStock(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.Stocks;
}

function _isPrivateEquity(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.PrivateEquity;
}

function _isDiversifiedCredit(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.DiversifiedCredit;
}

function _isPrivateCredit(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.PrivateCredit;
}

function _isVentureCapital(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.VentureCapital;
}

function _isRealEstate(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.RealEstate;
}

function _isRepurchaseAgreements(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.RepurchaseAgreements;
}

function _isAssetBackedCredit(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.AssetBackedCredit;
}

function _isSpecialtyFinance(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.SpecialtyFinance;
}

function _isNonUSGovernmentDebt(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.NonUSGovernmentDebt;
}

function _isMunicipalBonds(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.MunicipalBonds;
}

function _isCorporateCredit(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.CorporateCredit;
}

function _isActiveStrategy(props: { assetClassID: number }): boolean {
    return props.assetClassID === ASSET_CLASS_IDS.ActiveStrategies;
}

/**
 * getValueByAssetClass
 * Returns a value based on the Asset class of props.asset
 * Includes support for a fallback value for type-safety
 */
export function getValueByAssetClassID<T>(props: {
    assetClassID: number | null;
    values: {
        treasury?: T;
        stablecoin?: T;
        commodity?: T;
        stocks?: T;
        private_equity?: T;
        venture_capital?: T;
        private_credit?: T;
        real_estate?: T;
        non_us_government_debt?: T;
        municipal_bonds?: T;
        corporate_credit?: T;
        diversified_credit?: T;
        active_strategies?: T;
        repurchase_agreements?: T;
        asset_backed_credit?: T;
        specialty_finance?: T;
    };
    fallbackValue: T;
}): T {
    const { assetClassID, values, fallbackValue } = props;

    if (assetClassID == null) {
        return fallbackValue;
    }

    if (_isStablecoin({ assetClassID })) {
        return values.stablecoin || fallbackValue;
    }

    if (_isTreasuryProduct({ assetClassID })) {
        return values.treasury || fallbackValue;
    }

    if (_isCommodity({ assetClassID })) {
        return values.commodity || fallbackValue;
    }

    if (_isStock({ assetClassID })) {
        return values.stocks || fallbackValue;
    }

    if (_isPrivateEquity({ assetClassID })) {
        return values.private_equity || fallbackValue;
    }

    if (_isVentureCapital({ assetClassID })) {
        return values.venture_capital || fallbackValue;
    }

    if (_isDiversifiedCredit({ assetClassID })) {
        return values.diversified_credit || fallbackValue;
    }

    if (_isActiveStrategy({ assetClassID })) {
        return values.active_strategies || fallbackValue;
    }

    // Handle Non-US Government Debt override
    if (
        _isNonUSGovernmentDebt({ assetClassID }) &&
        values.non_us_government_debt
    ) {
        return values.non_us_government_debt || fallbackValue;
    }

    // Handle Municipal Bonds override
    if (_isMunicipalBonds({ assetClassID }) && values.municipal_bonds) {
        return values.municipal_bonds || fallbackValue;
    }

    // Handle Corporate Credit override
    if (_isCorporateCredit({ assetClassID }) && values.corporate_credit) {
        return values.corporate_credit || fallbackValue;
    }

    if (_isPrivateCredit({ assetClassID })) {
        return values.private_credit || fallbackValue;
    }

    if (_isRealEstate({ assetClassID })) {
        return values.real_estate || fallbackValue;
    }

    if (_isRepurchaseAgreements({ assetClassID })) {
        return values.repurchase_agreements || fallbackValue;
    }

    if (_isAssetBackedCredit({ assetClassID })) {
        return values.asset_backed_credit || fallbackValue;
    }

    if (_isSpecialtyFinance({ assetClassID })) {
        return values.specialty_finance || fallbackValue;
    }

    return fallbackValue;
}
